<script setup lang="ts">
import {Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,} from "@/core/components/ui/dialog";
import {computed, ref} from "vue";
import ConsumerProductChip from "@/drug-interaction/components/header/ConsumerProductChip.vue";
import DosageForm from "@/dosage/components/DosageForm.vue";
import DosagePrescriptions from "@/drug-interaction/components/dosage/DosagePrescriptions.vue";
import {Button} from "@/core/components/ui/button";
import {File} from "@lucide/vue";
import DosageTemplateSelector from "@/dosage/components/DosageTemplateSelector.vue";
import Dosage from "@/dosage/interfaces/Dosage";
import BaseButton from "@/core/components/base/BaseButton.vue";
import {DosageFormData} from "@/dosage/interfaces/DosageForm";
import {useToastStore} from "@/stores/toast.store";
import {dosageApi} from "@/container";
import {ProductWithLatestDosage} from "@/consumer/interfaces";
import DosageFormService from "@/dosage/services/DosageFormService";

const {showError, showSuccess} = useToastStore();
const emits = defineEmits<{
    (e: 'submitted'): void;
}>();
const open = defineModel<boolean>("open", {default: false});
const lastPrescriptionDate = ref<Date | null>(null);
const form = ref<InstanceType<typeof DosageForm> | null>(null);
const formData = ref<DosageFormData>(DosageFormService.defaultFormValue());
const isSubmitting = ref(false);

const props = defineProps<{
    products: ProductWithLatestDosage[];
    consumerKey: string;
}>();

/**
 * The current product index for dosage.
 */
const currentProductIndex = ref<number | null>(props.products.length ? 0 : null);

/**
 * The current product for dosage.
 */
const currentProduct = computed(() => {
    if (currentProductIndex.value === null || currentProductIndex.value >= props.products.length) {
        return null;
    }

    return props.products[currentProductIndex.value];
});

/**
 * Determine if the current product is the last one from the product list.
 */
const currentProductIsLast = computed(() => {
    return currentProductIndex.value !== null && currentProductIndex.value === props.products.length - 1;
});

/**
 * Scroll to the prescriptions section.
 */
const scrollToPrescriptions = () => {
    const element = document.getElementById('dosage-prescriptions');
    if (element) {
        element.scrollIntoView({behavior: 'smooth'});
    }
};

/**
 * When template is selected, prefill the form with its data.
 */
const handleTemplateSelect = (template: Dosage) => {
    if (form.value) {
        form.value.prefill(template);
    }
};

/**
 * Whether the submit button should be disabled.
 */
const isSubmitDisabled = computed(() => {
    return !formData.value || isSubmitting.value;
});

/**
 * If the current product is the last one, close the dialog.
 * Otherwise, go to the next product.
 */
const nextAction = (): void => {
    if (currentProductIsLast.value) {
        open.value = false;
    } else if (currentProductIndex.value !== null) {
        currentProductIndex.value++;
    }
};

/**
 * Handle the form submission.
 */
const handleSubmit = async () => {
    if (!form.value?.validate()) {
        return;
    }

    if (formData.value && currentProduct.value) {
        try {
            isSubmitting.value = true;

            const payload = DosageFormService.mapDosageFormDataToPayload(
                currentProduct.value.id,
                props.consumerKey,
                formData.value
            );

            if (currentProduct.value.latest_dosage) {
                await dosageApi().updateDosage(currentProduct.value.latest_dosage.id, payload);
            } else {
                await dosageApi().createDosage(payload);
            }

            emits('submitted');
            showSuccess({
                content: "La posologie a été enregistrée avec succès.",
            });

            nextAction();
        } catch {
            showError({
                content: "Une erreur est survenue lors de l'enregistrement de la posologie.",
            });
        } finally {
            isSubmitting.value = false;
        }
    }
};
</script>

<template>
  <Dialog v-model:open="open" modal>
    <DialogContent
      class="p-0 gap-0 overflow-hidden min-w-[75%] max-h-[80vh] flex flex-col"
      @interact-outside.prevent
      @open-auto-focus.prevent
    >
      <DialogHeader class="border-b-[1px] border-black p-3">
        <div class="text-start space-y-1">
          <DialogTitle as-child>
            <h1>
              Plan de posologie
            </h1>
          </DialogTitle>
          <DialogDescription as-child>
            <p v-if="products.length > 1 && currentProductIndex !== null">
              {{ currentProductIndex + 1 }}/{{ products.length }} traitements
            </p>
          </DialogDescription>
        </div>
      </DialogHeader>
      <div v-if="currentProduct && currentProductIndex !== null" class="bg-gray-100 p-3 space-y-3 overflow-y-auto">
        <div class="flex justify-between flex-col md:flex-row gap-x-4">
          <div class="space-y-1">
            <ConsumerProductChip :index="currentProductIndex" :product="currentProduct" class="w-auto" />
            <Button variant="ghost" class="text-primary align-baseline" @click="scrollToPrescriptions">
              <File class="text-black size-4" />
              Voir les ordonnances
            </Button>
          </div>
          <DosageTemplateSelector class="self-start" :product-id="currentProduct.id" @select="handleTemplateSelect" />
        </div>
        <Transition name="slide" mode="out-in">
          <div :key="currentProduct.id" class="space-y-4">
            <div class="bg-white rounded-lg p-3" tabindex="-1">
              <DosageForm
                ref="form"
                :key="currentProduct.id"
                v-model="formData"
                :product="currentProduct"
                :last-prescription-date="lastPrescriptionDate"
              />
            </div>
            <DosagePrescriptions
              id="dosage-prescriptions" :consumer-key="consumerKey"
              :product="currentProduct"
              @last-date="date => lastPrescriptionDate = date"
            />
          </div>
        </Transition>
      </div>
      <div class="flex justify-end p-4 items-center gap-4">
        <div>
          <p v-if="products.length === 1 || currentProductIsLast" class="text-sm text-gray-600">
            Vous pourrez vérifier le pilulier du patient après l'enregistrement
          </p>
        </div>
        <BaseButton primary :disabled="isSubmitDisabled" :loading="isSubmitting" @click="handleSubmit">
          {{ products.length === 1 ? 'Enregistrer' : 'Enregistrer et continuer' }}
        </BaseButton>
      </div>
    </DialogContent>
  </Dialog>
</template>

<style scoped>
.slide-enter-active,
.slide-leave-active {
    transition: all 0.3s ease;
}

.slide-enter-from {
    opacity: 0;
    transform: translateX(30px);
}

.slide-leave-to {
    opacity: 0;
    transform: translateX(-30px);
}

.slide-enter-to,
.slide-leave-from {
    opacity: 1;
    transform: translateX(0);
}
</style>
